import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api, type Page } from '../api/client';
import { useStudio } from './App';
import type { Asset } from './production';

export function mergeAssets(pages: Asset[][], bound: Asset[] = []) {
  const records = new Map<string, Asset>();
  for (const asset of [...pages.flat(), ...bound]) records.set(asset.id, asset);
  return [...records.values()];
}

export function useBoundAssets(seriesId: string | undefined, assetIds: string[]) {
  const { base } = useStudio();
  const ids = [...new Set(assetIds.filter(Boolean))].sort();
  return useQuery({
    queryKey: ['api', `${base}/assets`, 'bound', seriesId, ids.join(',')],
    enabled: !!seriesId && ids.length > 0,
    queryFn: async () => {
      const chunks = [];
      for (let offset = 0; offset < ids.length; offset += 100) {
        chunks.push(
          api<Page<Asset>>(
            `${base}/assets?seriesId=${seriesId}&ids=${encodeURIComponent(ids.slice(offset, offset + 100).join(','))}`,
          ),
        );
      }
      return { items: (await Promise.all(chunks)).flatMap((page) => page.items) };
    },
  });
}

export function useSeriesAssets(seriesId: string | undefined, assetIds: string[] = []) {
  const { base } = useStudio();
  const catalog = useInfiniteQuery({
    queryKey: ['api', `${base}/assets`, 'catalog', seriesId],
    enabled: !!seriesId,
    initialPageParam: '',
    queryFn: ({ pageParam }) =>
      api<Page<Asset>>(
        `${base}/assets?seriesId=${seriesId}&limit=100${pageParam ? `&cursor=${encodeURIComponent(pageParam)}` : ''}`,
      ),
    getNextPageParam: (page) => page.nextCursor || undefined,
  });
  const bound = useBoundAssets(seriesId, assetIds);
  return {
    ...catalog,
    data: {
      items: mergeAssets(catalog.data?.pages.map((page) => page.items) ?? [], bound.data?.items),
    },
    error: catalog.error ?? bound.error,
    boundPending: assetIds.filter(Boolean).length > 0 && bound.isPending,
    refetch: async () => {
      await Promise.all([
        catalog.refetch(),
        ...(assetIds.filter(Boolean).length ? [bound.refetch()] : []),
      ]);
    },
  };
}
